<template>
    <div :dir="$root.$i18n.locale == 'ar' ? 'rtl' : 'ltr'">
        <div class="tw-grid tw-grid-cols-12 tw-min-h-[100dvh] tw-relative">
            <div class="tw-relative tw-pb-24 tw-col-span-12 md:tw-col-span-7 lg:tw-col-span-5 2xl:tw-col-span-4 tw-h-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-flex-col tw-p-5">
                
                
                <form @submit="verifyCode" class="tw-w-full lg:tw-max-w-[350px] tw-mx-auto" action="">
                    <div class="tw-w-full lg:tw-max-w-[350px]">
                        <AppLogo />
                    </div>

                    <h1 class="tw-mt-5 lg:tw-text-3xl tw-text-xl tw-w-full tw-text-start tw-font-bold tw-text-gray-800">
                        {{ $t('pages.email-confirmation.welcome') }} <span class="tw-text-primary-500">!</span>
                    </h1>
                    <p class="tw-mb-7 tw-mt-2 tw-text-md tw-font-semibold">{{ $t('pages.email-confirmation.welcome-paragraph') }}</p>

                    <div class="tw-mt-5">
                        <OtpInput
                            v-model="verificationCode"
                            input-dir="ltr"
                            hide-label
                            :label="$t('pages.email-confirmation.code-sent')"
                            :error="errors.code && $t('errors.' + errors.code, { field: 'verification code' })"
                            :disabled="isFetching"
                        />

                        <div class="tw-flex tw-w-full tw-justify-center tw-mt-4">
                            <button 
                                type="button"
                                @click="resendCode"
                                :disabled="isResending || resendCooldown > 0"
                                class="tw-text-primary-500 tw-text-sm tw-font-semibold tw-duration-200 hover:tw-text-primary-600 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed"
                            >
                                {{ resendCooldown > 0 ? `${$t('pages.email-confirmation.resend-code')} (${resendCooldown}s)` : $t('pages.email-confirmation.resend-code') }}
                            </button>
                        </div>

                        <div class="tw-mb-7"></div>

                        <a-button 
                            intent="primary" 
                            :loading="isFetching" 
                            :disabled="isFetching || verificationCode.length !== 6" 
                            full
                        >
                            {{ $t('pages.email-confirmation.verify-button') }}
                        </a-button>
                        
                        <div class="tw-flex tw-w-full tw-text-sm tw-justify-center tw-text-gray-700 tw-mt-3">
                            <p>
                                {{ $t('pages.register.already-have-account') }}
                                <router-link 
                                    :to="{ name: 'auth.login' }"
                                    type="button"
                                    class="tw-text-primary-500 tw-px-1 tw-font-semibold tw-duration-200 hover:tw-text-primary-600"
                                >{{ $t('buttons.sign-in') }}</router-link>
                            </p>
                        </div>
                    </div>
                </form>

                <div class="tw-flex tw-items-center tw-justify-between tw-absolute tw-bottom-0 tw-p-5 tw-text-center tw-w-full">
                    <p class="tw-text-sm tw-text-gray-600">{{ $t('pages.copyright', { year: (new Date()).getFullYear(), appName: APP_NAME }) }}</p>
                    <AppLanguageSwitcher />
                </div>
            </div>

            <div class="tw-col-span-12 md:tw-col-span-5 lg:tw-col-span-7 2xl:tw-col-span-8 tw-relative tw-h-full tw-hidden md:tw-block">
                <img :src="$frontend('assets/images/backgrounds/login-bg.jpg')" class="tw-h-full tw-w-full tw-object-cover tw-bg-left">
                <div class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-z-10 tw-bg-gradient-to-t tw-from-black/80 tw-via-primary-900/70 tw-to-primary-600/40 tw-flex tw-items-center tw-justify-center tw-p-8 lg:tw-p-12">
                    <div class="tw-text-center tw-text-white tw-max-w-lg">
                        <icon icon="solar:letter-unread-bold-duotone" class="tw-text-8xl tw-mb-6 tw-text-white tw-drop-shadow-lg"/>
                        <h2 class="tw-text-4xl lg:tw-text-5xl tw-font-bold tw-mb-4 tw-leading-tight tw-drop-shadow-lg">
                            {{ $t('pages.login.overlay-heading') }}
                        </h2>
                        <p class="tw-text-lg lg:tw-text-xl tw-font-medium tw-opacity-95 tw-leading-relaxed tw-drop-shadow-md">
                            {{ $t('pages.login.overlay-description') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>
<script setup>
import OtpInput from '@/components/form/OtpInput'
import AppLogo from '@/components/AppLogo'
import AppLanguageSwitcher from '@/components/AppLanguageSwitcher'
import { APP_NAME } from '@/config/app'
import { verifyOtp, resendOtp } from '@/api/auth'
import { useAuthStore } from '@/stores'
import useAlert from '@/composables/useAlert'
import { reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const store = useAuthStore()
const { t } = useI18n()

const verificationCode = ref('')
const email = ref('')
const isFetching = ref(false)
const isResending = ref(false)
const resendCooldown = ref(0)

const errors = reactive({
    code: ''
})

const mapError = (error) => {
    if (error?.response?.status == 422 && error.response.data.errors?.code) {
        errors.code = error.response.data.errors.code[0]
    } else {
        errors.code = 'something-wrong-happened'
    }
}

const verifyCode = async ($e) => {
    $e.preventDefault()

    errors.code = ''

    if (!verificationCode.value || verificationCode.value.length !== 6) {
        errors.code = 'required'
        return false
    }

    isFetching.value = true

    try {
        const response = await verifyOtp({
            email: email.value,
            code: verificationCode.value
        })

        if (response.data.code === 'SUCCESS') {
            // Verified + logged in server-side; hydrate the store and go.
            store.setUser(response.data.data.user, true)
            try { sessionStorage.removeItem('pending_verification_email') } catch (e) { /* noop */ }
            router.push({ name: 'dashboard' })
        }
    } catch (error) {
        mapError(error)
    } finally {
        isFetching.value = false
    }
}

const resendCode = async () => {
    if (resendCooldown.value > 0 || isResending.value) return

    isResending.value = true
    errors.code = ''

    try {
        await resendOtp({ email: email.value })
        useAlert(t('pages.email-confirmation.resend-success'), 'success')
        startResendCooldown()
    } catch (error) {
        mapError(error)
    } finally {
        isResending.value = false
    }
}

const startResendCooldown = () => {
    resendCooldown.value = 60 // 60 seconds cooldown
    const interval = setInterval(() => {
        resendCooldown.value--
        if (resendCooldown.value <= 0) {
            clearInterval(interval)
        }
    }, 1000)
}

onMounted(() => {
    try {
        email.value = sessionStorage.getItem('pending_verification_email') || ''
    } catch (e) {
        email.value = ''
    }

    // No pending registration to confirm — send them back to sign up.
    if (!email.value) {
        router.replace({ name: 'auth.visitor' })
    }
})
</script>
<style>
    
</style>